//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { ProductDetailsTab } from '@campaigns/components/shared/product-details-modal/drawer/tabs/ProductDetailsTab';
import { VariantsTab } from '@campaigns/components/shared/product-details-modal/drawer/tabs/VariantsTab';
import { ProductDetailsModalAlerts } from '@campaigns/components/shared/product-details-modal/modal/ProductDetailsModalAlerts';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { getBVProductFormErrorMessages } from '@campaigns/utils/FormUtils';

export enum ProductDetailsDrawerPanel {
  ProductDetails = 'product-details',
  Variants = 'variants',
}

interface ProductDetailsDrawerContentProps {
  currentPanel: ProductDetailsDrawerPanel;
  isSubmitting?: boolean;
  isViewOnly: boolean;
  onEditVariants: () => void;
}

// Content tree for the drawer experience. Mirrors modal/ProductDetailsModalContent
// but renders one panel at a time instead of tabs (see ProductDetailsDrawer for the
// panel navigation/footer logic).
export const ProductDetailsDrawerContent = ({
  currentPanel,
  isSubmitting = false,
  isViewOnly,
  onEditVariants,
}: ProductDetailsDrawerContentProps) => {
  const {
    formState: { errors },
  } = useFormContext<EditableBVSamplingProduct>();

  const errorMessages = getBVProductFormErrorMessages(
    errors,
    ProductDetailsFieldLabels,
  );

  return (
    <>
      <ProductDetailsModalAlerts
        errorMessages={errorMessages}
        isViewOnly={isViewOnly}
        shouldShowTeamMessage={false}
      />
      {/*
        Both panels stay mounted so switching between them doesn't reset local component
        state (e.g. ProductImageUpload's file/URL mode) or React Hook Form validation state.
      */}
      <HiddenPanel
        $isHidden={currentPanel !== ProductDetailsDrawerPanel.ProductDetails}
      >
        <ProductDetailsTab
          isSubmitting={isSubmitting}
          isViewOnly={isViewOnly}
          onEditVariants={onEditVariants}
        />
      </HiddenPanel>
      <HiddenPanel
        $isHidden={currentPanel !== ProductDetailsDrawerPanel.Variants}
      >
        <VariantsTab isViewOnly={isViewOnly} />
      </HiddenPanel>
    </>
  );
};

const HiddenPanel = styled.div<{ $isHidden: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`;
